"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import FAQList from "@/components/global/ui/FAQList";
import { PortableTextBlock } from "sanity";
import { PageAffiliateV2Schema } from "@/sanity/pages/PageAffiliateV2Template";
import iqicon from "@/public/affiliate/iqIcon.svg";
import { CTAButton } from "@/shadcn/ui/button";
import { Fragment } from "react";

function Quote() {
  return (
    <svg
      className="w-[4rem] shrink-0 mt-[1.125rem]"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="29"
      viewBox="0 0 32 29"
      fill="none"
    >
      <path
        d="M19.2412 29L24.3859 12.9117H18.7267V0H32V11.3746L25.5177 29H19.2412ZM0.514469 29L5.65916 12.9117H0V0H13.2733V11.3746L6.791 29H0.514469Z"
        fill="#6DA991"
      />
    </svg>
  );
}

export default function ExclusiveSection({
  data,
  className,
}: {
  data: PageAffiliateV2Schema;
  className?: string;
}) {
  return (
    <section
      className={cn("relative overflow-clip  px-[2rem] lg:px-0", className)}
    >
      <div className="w-[min(99.375rem,100%)] mx-auto flex flex-col items-start lg:p-[6.75rem] relative bg-white lg:border border-[#E5E5E5]">
        {data.exclusiveTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext mb-[4.375rem]"
          >
            <PortableText
              value={data.exclusiveTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
        {data.exclusiveTestimonials && (
          <div className="flex flex-col gap-[2rem] mb-[4.375rem]">
            {data.exclusiveTestimonials.map((item, index) => (
              <motion.div
                key={"exclusiveTest" + index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-[#6DA991]/[0.14] border-l-[0.25rem] border-[#6DA991] rounded-[1.5rem] p-[2.5rem] lg:p-[4.5rem] flex gap-[5rem]"
                //
              >
                <Quote />
                <div className="flex flex-col gap-[3rem]">
                  <div className="richtext richtext-start prose-p:text-[#041726] prose-p:text-[2rem] lg:prose-p:text-[2.25rem]">
                    <PortableText
                      value={item.text}
                      components={richTextComponent}
                    />
                  </div>
                  <span className="text-[#041726] opacity-[0.35] text-[2.5rem]">
                    {item.name}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex flex-col w-full gap-[2rem]">
          {data?.exclusiveItems &&
            data?.exclusiveItems.map((item, index) => (
              <Fragment key={"f" + index}>
                <motion.div
                  key={index}
                  className="flex items-center w-full gap-[2.25rem] lg:gap-[3.25rem]"
                  initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                  whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  <Image
                    src={item.icon}
                    alt=""
                    className="!w-[6rem] shrink-0"
                  />
                  <p className="m-0 text-[#103037]">{item.text}</p>
                </motion.div>
                {index !== data.exclusiveItems.length - 1 && (
                  <hr className="w-full border-t border-black/10" />
                )}
              </Fragment>
            ))}
          <CTAButton
            data={data.exclusiveCta}
            className="w-full mt-[3rem] lg:w-fit lg:min-w-[40rem]"
          />
        </div>
      </div>
    </section>
  );
}
